// client/src/components/pages/OperationOverview/ExecutiveSummary.tsx (converted from .js, ST-00216)
import { useState, useEffect, useMemo, type ComponentType } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Treemap,
} from 'recharts';
import type { TooltipProps } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { HiOutlineArrowRight } from 'react-icons/hi2';
import api from '../../../services/api';
import LexiBridgeLoaderJs from '../../common/LexiBridgeLoader';
import AlertBannerJs from './AlertBanner';
import { useThemeTokens } from '../../../utils/themeTokens';
import { dispatchedExplanation, executiveFigures, includedLossesText } from './executiveFigures';
import MatchingNote from './MatchingNote';
import { adjustedDispatch, fallbackNoteOf, isShownAsCard, naReasonOf } from './overviewKpis';
import { routeVariance } from './routeSort';
import type {
  ExecutiveSummaryResponse,
  OverviewAlert,
  OverviewGrouping,
} from '../../../types/clientPages/operationOverview';
import { NO_VARIANCE_BASE_TEXT } from '../../../utils/variance';

// Typed views of the JS components.
const LexiBridgeLoader = LexiBridgeLoaderJs as unknown as ComponentType<{
  message?: string;
  compact?: boolean;
}>;
const AlertBanner = AlertBannerJs as unknown as ComponentType<{ alerts?: OverviewAlert[] }>;

const TEAL = '#45B7AA';
const DISP = '#e8943a';
const RECV = '#45B7AA';

const PRODUCT_CLR = [
  '#45B7AA',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#f59e0b',
  '#06b6d4',
  '#10b981',
  '#6366f1',
  '#ef4444',
  '#14b8a6',
  '#a855f7',
  '#f97316',
  '#0ea5e9',
  '#84cc16',
  '#d946ef',
];

const n = (v: number | null | undefined, d = 0): string =>
  v == null || isNaN(v) ? '—' : Number(v).toLocaleString('en-ZA', { maximumFractionDigits: d });
const nK = (v: number | null | undefined): string => {
  if (v == null) return '—';
  const a = Math.abs(v);
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return n(v);
};
const pct = (v: number): string => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
const ds = (d: string | number | Date): string =>
  new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' });
const toISO = (d: Date | string | null | undefined): string => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};
const vClr = (p: number): string =>
  Math.abs(p) > 5 ? '#dc2626' : Math.abs(p) > 3 ? '#d97706' : '#16a34a';
/** A variance that is n/a (ST-00298): neutral slate. */
const NA_CLR = '#64748b';
const vBg = (p: number): string =>
  Math.abs(p) > 5
    ? 'bg-red-50 text-red-700 border-red-100'
    : Math.abs(p) > 3
      ? 'bg-amber-50 text-amber-700 border-amber-100'
      : 'bg-emerald-50 text-emerald-700 border-emerald-100';

/* ── Chart tooltip ─────────────────────────────────────── */
const TrendTip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (!active || !payload?.length) return null;
  const d = payload.find((p) => p.dataKey === 'dispatchWeight')?.value || 0;
  const r = payload.find((p) => p.dataKey === 'receiptWeight')?.value || 0;
  const diff = r - d;
  const v = d > 0 ? (diff / d) * 100 : 0;

  return (
    <div className="bg-surface rounded-lg shadow-lg border border-slate-200 px-3.5 py-2.5 min-w-[200px]">
      <p className="font-display text-[11px] font-bold text-slate-700 mb-2">{ds(label)}</p>
      {[
        { label: 'Dispatched', val: d, clr: DISP },
        { label: 'Received', val: r, clr: RECV },
      ].map((row) => (
        <div key={row.label} className="flex items-center justify-between py-0.5">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-[3px] rounded-full" style={{ background: row.clr }} />
            <span className="text-[11px] text-slate-500">{row.label}</span>
          </div>
          <span className="font-mono text-[11px] font-semibold text-slate-800 tabular-nums">
            {n(row.val)} MT
          </span>
        </div>
      ))}
      <div className="mt-1.5 pt-1.5 border-t border-slate-100 flex items-center justify-between">
        <span className="text-[10px] text-slate-400">Variance</span>
        <span className="font-mono text-[11px] font-bold tabular-nums" style={{ color: vClr(v) }}>
          {diff >= 0 ? '+' : ''}
          {n(diff)} MT ({pct(v)})
        </span>
      </div>
    </div>
  );
};

/* ── Treemap cell ──────────────────────────────────────── */
/** The node layout recharts' Treemap passes to its `content` element. */
interface TreeCellProps {
  x: number;
  y: number;
  width: number;
  height: number;
  name?: string;
  value?: number;
  index: number;
}
const TreeCell = (props: Partial<TreeCellProps>) => {
  const { x, y, width, height, name, value, index } = props as TreeCellProps;
  const t = useThemeTokens();
  if (width < 4 || height < 4) return null;
  const clr = PRODUCT_CLR[index % PRODUCT_CLR.length];
  const canLabel = width > 50 && height > 30;
  const canVal = width > 50 && height > 46;
  const maxC = Math.floor((width - 14) / 6);

  return (
    <g>
      <rect
        x={x + 1}
        y={y + 1}
        width={width - 2}
        height={height - 2}
        rx={6}
        fill={clr}
        fillOpacity={0.12}
        stroke={clr}
        strokeWidth={1}
        strokeOpacity={0.25}
      />
      {canLabel && (
        <text
          x={x + 8}
          y={y + 17}
          fill={t.textStrong}
          fontSize={10}
          fontWeight="600"
          fontFamily="'DM Sans', sans-serif"
        >
          {name && name.length > maxC ? name.slice(0, maxC) + '…' : name}
        </text>
      )}
      {canVal && (
        <text
          x={x + 8}
          y={y + 30}
          fill={t.axisLabel}
          fontSize={9}
          fontFamily="'IBM Plex Mono', monospace"
        >
          {nK(value)} MT
        </text>
      )}
    </g>
  );
};

/** One row of the Variance Exposure list (pct null = n/a: no receipt data). */
interface VarianceExposureRow {
  name: string;
  dispatched: number;
  received: number;
  diff: number;
  pct: number | null;
  /** Why `pct` is n/a, in words; null when there is one. */
  naReason: string | null;
}

export interface ExecutiveSummaryProps {
  startDate: Date;
  endDate: Date;
  alerts: OverviewAlert[];
  groupings?: OverviewGrouping[];
}

/* ═════════════════════════════════════════════════════════ */
const ExecutiveSummary = ({
  startDate,
  endDate,
  alerts,
  groupings = [],
}: ExecutiveSummaryProps) => {
  const navigate = useNavigate();
  const t = useThemeTokens();
  const [data, setData] = useState<ExecutiveSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!startDate || !endDate) return;
    let dead = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.routeSummary.getExecutiveSummary(toISO(startDate), toISO(endDate));
        if (!dead) setData(r.data as ExecutiveSummaryResponse);
      } catch {
        if (!dead) setError('Failed to load executive summary');
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => {
      dead = true;
    };
  }, [startDate, endDate]);

  const varExposure = useMemo((): VarianceExposureRow[] => {
    if (!groupings?.length) return [];
    return (
      groupings
        .filter((g) => g.totalDispatchWeight > 0 && g.includeInReporting !== false)
        .map((g) => {
          // The server's adjusted dispatch (ST-00218 P3), else dispatched + BF − CF.
          const adjDispatch = adjustedDispatch(g);
          const pct = routeVariance(g);
          return {
            name: g.name,
            dispatched: adjDispatch,
            received: g.totalReceiptWeight || 0,
            diff: (g.totalReceiptWeight || 0) - adjDispatch,
            pct,
            naReason: pct === null ? naReasonOf(g) : null,
          };
        })
        // Worst first; routes without a variance (no receipt data, nothing left dispatched) last.
        .sort((a, b) =>
          a.pct === null || b.pct === null
            ? a.pct === b.pct
              ? 0
              : a.pct === null
                ? 1
                : -1
            : a.diff - b.diff
        )
    );
  }, [groupings]);

  // maxAbsDiff: computed but never read (kept as a call so the hooks are unchanged).
  useMemo(() => Math.max(...varExposure.map((v) => Math.abs(v.diff)), 1), [varExposure]);
  const maxDispatched = useMemo(
    () => Math.max(...varExposure.map((v) => v.dispatched), 1),
    [varExposure]
  );

  const treemapData = useMemo(() => {
    if (!data?.productBreakdown) return [];
    return data.productBreakdown
      .filter((p) => p.totalWeight > 0)
      .slice(0, 15)
      .map((p) => ({ name: p.product, value: Math.round(p.totalWeight) }));
  }, [data]);

  const treemapTotal = useMemo(() => treemapData.reduce((s, x) => s + x.value, 0), [treemapData]);

  if (loading)
    return (
      <div className="flex items-center justify-center py-32 bg-slate-50">
        <LexiBridgeLoader message="Building executive summary" />
      </div>
    );
  if (error || !data?.summary)
    return (
      <div className="max-w-[1800px] mx-auto px-6 py-8 bg-slate-50">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-medium">{error || 'No data available for this period'}</p>
        </div>
      </div>
    );

  const { summary, dailyVolumes } = data;
  // ST-00215 (RSO-7): the same figures as the Route Detail headline — see
  // executiveFigures.ts. Dispatched is after Brought / Carried Forward. ST-00298: the net
  // variance leaves out the routes that received against nothing left dispatched.
  const fig = executiveFigures(summary, groupings);
  const diffPct = fig.variancePct;
  const netTitle =
    diffPct === null
      ? `Not available: ${NO_VARIANCE_BASE_TEXT}`
      : fig.varianceExcludedRoutes > 0
        ? `Leaves out ${fig.varianceExcludedRoutes} route${fig.varianceExcludedRoutes === 1 ? '' : 's'}: ${NO_VARIANCE_BASE_TEXT}`
        : undefined;
  const netClr = diffPct === null ? NA_CLR : vClr(diffPct);
  const dispPct = fig.dispatchShare;
  const periodLabel =
    startDate && endDate
      ? `${ds(startDate)} — ${new Date(endDate).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })}`
      : '';
  // Routes counted in these figures = the route cards on Route Detail.
  const activeRoutes =
    fig.routeCount !== null ? fig.routeCount : groupings.filter(isShownAsCard).length;
  const bfcfSub = !fig.hasBfcf
    ? `${n(Math.round(fig.avgDailyDispatch))} MT/day`
    : fig.hasDeductions
      ? `incl. adjustments ${fig.adjustmentNet >= 0 ? '+' : '−'}${n(Math.abs(fig.adjustmentNet))} MT`
      : `incl. BF/CF ${fig.bfcfNet >= 0 ? '+' : '−'}${n(Math.abs(fig.bfcfNet))} MT`;
  // ST-00218 P3: how Dispatched is built, and the missing / needs-review loads it includes.
  const explanation = dispatchedExplanation(fig, (v) => n(v));
  const included = includedLossesText(fig, (v) => n(v));
  const matchingNote = fallbackNoteOf(null, summary);

  return (
    <div className="bg-slate-50">
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-3 space-y-3">
        <AlertBanner alerts={alerts} />
        <MatchingNote note={matchingNote} />

        {/* ── HERO STRIP ──────────────────────────────── */}
        <div className="bg-surface rounded-xl border border-slate-200 shadow-sm px-4 sm:px-5 py-4">
          {/* Top: period + headline number inline */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div className="flex items-center gap-6">
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
                  <span className="text-[10px] font-display font-semibold text-slate-400 uppercase tracking-[0.1em]">
                    {periodLabel}
                  </span>
                  <span className="text-slate-300 text-[10px]">·</span>
                  <span className="text-[10px] text-slate-400 font-mono">
                    {summary.daysWithData}d
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span
                    className="font-display text-3xl sm:text-4xl font-black tracking-tight"
                    style={{ color: TEAL }}
                  >
                    {n(fig.dispatched)}
                  </span>
                  <span className="text-sm text-slate-300 font-display font-light">
                    MT dispatched
                  </span>
                </div>
              </div>
              {/* Split bar — compact */}
              <div className="hidden md:block w-48 lg:w-64">
                <div className="flex h-1.5 rounded-full overflow-hidden mb-1.5">
                  <div
                    className="h-full rounded-l-full"
                    style={{ width: `${dispPct}%`, background: DISP }}
                  />
                  <div
                    className="h-full rounded-r-full"
                    style={{ width: `${100 - dispPct}%`, background: RECV }}
                  />
                </div>
                <div className="flex items-center justify-between text-[9px]">
                  <div className="flex items-center gap-1">
                    <span className="w-1 h-1 rounded-full" style={{ background: DISP }} />
                    <span className="text-slate-400">Out {n(fig.dispatched)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-400">In {n(fig.received)}</span>
                    <span className="w-1 h-1 rounded-full" style={{ background: RECV }} />
                  </div>
                </div>
              </div>
            </div>
            {/* Variance badge (ST-00298: n/a, or naming the routes it leaves out) */}
            <div className="flex items-center gap-2" title={netTitle}>
              <span className="text-[10px] text-slate-400">Net variance</span>
              {diffPct !== null && (
                <span
                  className="font-mono text-sm font-bold tabular-nums"
                  style={{ color: netClr }}
                >
                  {fig.difference >= 0 ? '+' : ''}
                  {n(fig.difference)} MT
                </span>
              )}
              <span
                className="font-mono text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded-md border"
                style={{
                  color: netClr,
                  background: `${netClr}08`,
                  borderColor: `${netClr}25`,
                }}
              >
                {diffPct === null ? 'n/a' : pct(diffPct)}
              </span>
              {fig.varianceExcludedRoutes > 0 && (
                <span className="text-[10px] text-slate-400">
                  excl. {fig.varianceExcludedRoutes} n/a route
                  {fig.varianceExcludedRoutes === 1 ? '' : 's'}
                </span>
              )}
            </div>
          </div>

          {/* KPI row — horizontal strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              {
                label: 'Dispatched',
                val: n(fig.dispatched),
                sub: bfcfSub,
                accent: DISP,
              },
              {
                label: 'Received',
                val: n(fig.received),
                sub: `${n(Math.round(fig.avgDailyReceipt))} MT/day`,
                accent: RECV,
              },
              {
                label: 'Active Routes',
                val: String(activeRoutes),
                sub: `of ${groupings.length} configured`,
                accent: '#8b5cf6',
              },
              {
                label: 'Daily Throughput',
                val: n(Math.round(fig.avgDailyDispatch)),
                sub: 'MT dispatched / day',
                accent: '#6366f1',
              },
            ].map((k, i) => (
              <div
                key={i}
                className="bg-slate-50 rounded-lg border border-slate-100 px-3 py-2.5 relative overflow-hidden hover:border-slate-200 transition-colors"
              >
                <div
                  className="absolute top-0 left-0 w-[2.5px] h-full"
                  style={{ background: k.accent }}
                />
                <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5 pl-1.5">
                  {k.label}
                </p>
                <p className="font-display text-lg font-bold text-slate-800 tabular-nums tracking-tight pl-1.5 leading-tight">
                  {k.val}
                </p>
                <p className="text-[9px] font-mono tabular-nums pl-1.5" style={{ color: k.accent }}>
                  {k.sub}
                </p>
              </div>
            ))}
          </div>
          {(explanation || included) && (
            <div className="mt-2.5 space-y-0.5 text-[11px] text-slate-500 tabular-nums">
              {explanation && <p>{explanation}</p>}
              {included && <p>{included}</p>}
            </div>
          )}
        </div>

        {/* ── Row 1: Chart + Variance ─────────────────── */}
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-3">
          {/* Daily Tonnage */}
          <div className="xl:col-span-3 bg-surface rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-[13px] font-bold text-slate-800 font-display">Daily Tonnage</h3>
                <p className="text-[10px] text-slate-400">
                  Nett weight per operational day, as weighed (before brought / carried forward)
                </p>
              </div>
              <div className="flex items-center gap-4">
                {[
                  { l: 'Dispatch', c: DISP },
                  { l: 'Receipt', c: RECV },
                ].map((x) => (
                  <div key={x.l} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-[3px] rounded-full" style={{ background: x.c }} />
                    <span className="text-[10px] text-slate-400">{x.l}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="px-1 sm:px-2 py-2" style={{ height: 290 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyVolumes} margin={{ top: 8, right: 10, left: -2, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gD" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={DISP} stopOpacity={0.2} />
                      <stop offset="100%" stopColor={DISP} stopOpacity={0.01} />
                    </linearGradient>
                    <linearGradient id="gR" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={RECV} stopOpacity={0.15} />
                      <stop offset="100%" stopColor={RECV} stopOpacity={0.01} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={t.gridSubtle} strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={ds}
                    tick={{
                      fontSize: 9,
                      fill: t.axisLabel,
                      fontFamily: "'IBM Plex Mono', monospace",
                    }}
                    axisLine={{ stroke: t.grid }}
                    tickLine={false}
                    interval={Math.max(0, Math.floor(dailyVolumes.length / 10))}
                  />
                  <YAxis
                    tickFormatter={nK}
                    tick={{
                      fontSize: 9,
                      fill: t.axisLabel,
                      fontFamily: "'IBM Plex Mono', monospace",
                    }}
                    axisLine={false}
                    tickLine={false}
                    width={48}
                  />
                  <Tooltip
                    content={<TrendTip />}
                    cursor={{ stroke: t.axis, strokeDasharray: '3 3' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="dispatchWeight"
                    stroke={DISP}
                    strokeWidth={2}
                    fill="url(#gD)"
                    dot={false}
                    activeDot={{ r: 4, fill: t.surface, stroke: DISP, strokeWidth: 2 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="receiptWeight"
                    stroke={RECV}
                    strokeWidth={2}
                    fill="url(#gR)"
                    dot={false}
                    activeDot={{ r: 4, fill: t.surface, stroke: RECV, strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Variance Exposure */}
          <div className="xl:col-span-2 bg-surface rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-slate-100">
              <h3 className="text-[13px] font-bold text-slate-800 font-display">
                Variance Exposure
              </h3>
              <p className="text-[10px] text-slate-400">Route groupings — worst first</p>
            </div>
            <div
              className="flex-1 overflow-y-auto px-4 py-1"
              style={{ maxHeight: 260, scrollbarWidth: 'thin' }}
            >
              {varExposure.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-8">
                  No route groupings configured
                </p>
              ) : (
                varExposure.map((r, i) => {
                  const clr = r.pct === null ? 'var(--text-muted)' : vClr(r.pct);
                  return (
                    <div key={i} className="py-2 border-b border-slate-50 last:border-0">
                      <div className="flex items-center justify-between mb-1.5">
                        <span
                          className="text-xs font-medium text-slate-700 truncate max-w-[55%]"
                          title={r.name}
                        >
                          {r.name}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span
                            className="font-mono text-[11px] font-semibold tabular-nums"
                            style={{ color: clr }}
                          >
                            {r.diff >= 0 ? '+' : ''}
                            {n(r.diff)}
                          </span>
                          <span
                            className={`text-[9px] font-semibold font-mono tabular-nums px-1 py-px rounded border ${
                              r.pct === null
                                ? 'bg-slate-100 text-slate-700 border-slate-300'
                                : vBg(r.pct)
                            }`}
                            title={r.naReason ? `Variance not available: ${r.naReason}` : undefined}
                          >
                            {r.pct === null ? 'n/a' : pct(r.pct)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-[8px] text-slate-400 w-6 text-right flex-shrink-0 font-mono">
                          OUT
                        </span>
                        <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.max((r.dispatched / maxDispatched) * 100, 2)}%`,
                              background: DISP,
                              opacity: 0.55,
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-[8px] text-slate-400 w-6 text-right flex-shrink-0 font-mono">
                          IN
                        </span>
                        <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.max((r.received / maxDispatched) * 100, 2)}%`,
                              background: RECV,
                              opacity: 0.55,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* ── Row 2: Routes + Products ─────────────────── */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          {/* Tonnage by Route */}
          <div className="bg-surface rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-[13px] font-bold text-slate-800 font-display">
                  Tonnage by Route
                </h3>
                <p className="text-[10px] text-slate-400">Location pairs by dispatch volume</p>
              </div>
              <span className="text-[10px] font-mono text-slate-300 tabular-nums">
                {(data?.locationFlows || []).length} pairs
              </span>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: 310, scrollbarWidth: 'thin' }}>
              {(data?.locationFlows || []).length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-8">No route data</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/60">
                      <th className="text-left text-[9px] font-semibold uppercase tracking-wider text-slate-400 px-4 py-2">
                        Route
                      </th>
                      <th className="text-right text-[9px] font-semibold uppercase tracking-wider text-slate-400 px-2 py-2 hidden sm:table-cell">
                        Dispatched
                      </th>
                      <th className="text-right text-[9px] font-semibold uppercase tracking-wider text-slate-400 px-2 py-2 hidden sm:table-cell">
                        Received
                      </th>
                      <th className="text-right text-[9px] font-semibold uppercase tracking-wider text-slate-400 px-4 py-2">
                        Var.
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.locationFlows.slice(0, 12).map((f, i) => {
                      const diff = f.receiptWeight - f.dispatchWeight;
                      const v = f.dispatchWeight > 0 ? (diff / f.dispatchWeight) * 100 : 0;
                      return (
                        <tr
                          key={i}
                          className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors"
                        >
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="text-[11px] font-medium text-slate-700 truncate">
                                {f.dispatchLocation}
                              </span>
                              <svg
                                className="w-3 h-3 text-slate-300 flex-shrink-0"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M3 8h10M10 5l3 3-3 3"
                                  stroke="currentColor"
                                  strokeWidth="1.2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <span className="text-[11px] font-medium text-slate-700 truncate">
                                {f.receiptLocation}
                              </span>
                            </div>
                          </td>
                          <td className="px-2 py-2 text-right hidden sm:table-cell">
                            <span className="font-mono text-[11px] text-slate-600 tabular-nums">
                              {n(f.dispatchWeight)}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-right hidden sm:table-cell">
                            <span className="font-mono text-[11px] text-slate-600 tabular-nums">
                              {n(f.receiptWeight)}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <span
                              className={`font-mono text-[9px] font-semibold tabular-nums px-1 py-px rounded border ${vBg(v)}`}
                            >
                              {pct(v)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Product Mix */}
          <div className="bg-surface rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="text-[13px] font-bold text-slate-800 font-display">Product Mix</h3>
                <p className="text-[10px] text-slate-400">Tonnage by product</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-mono text-slate-300 tabular-nums">
                  {treemapData.length} products
                </span>
                <button
                  onClick={() => navigate('/product-movement')}
                  className="flex items-center gap-1 text-[10px] font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  View details <HiOutlineArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>
            <div className="p-3">
              {treemapData.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-8">No product data</p>
              ) : (
                <div className="flex flex-col xl:flex-row gap-3">
                  {/* Treemap */}
                  <div className="flex-1" style={{ minHeight: 200 }}>
                    <ResponsiveContainer width="100%" height={200}>
                      <Treemap
                        data={treemapData}
                        dataKey="value"
                        nameKey="name"
                        content={<TreeCell />}
                        animationDuration={600}
                      />
                    </ResponsiveContainer>
                  </div>
                  {/* Legend */}
                  <div className="xl:w-[180px] flex-shrink-0">
                    {treemapData.slice(0, 8).map((p, i) => {
                      const share =
                        treemapTotal > 0 ? ((p.value / treemapTotal) * 100).toFixed(1) : 0;
                      return (
                        <div
                          key={i}
                          className="flex items-center justify-between py-1 border-b border-slate-50 last:border-0"
                        >
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span
                              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{ background: PRODUCT_CLR[i % PRODUCT_CLR.length] }}
                            />
                            <span className="text-[10px] text-slate-600 truncate">{p.name}</span>
                          </div>
                          <span className="font-mono text-[9px] text-slate-400 tabular-nums flex-shrink-0 ml-1">
                            {share}%
                          </span>
                        </div>
                      );
                    })}
                    {treemapData.length > 8 && (
                      <p className="text-[9px] text-slate-300 mt-1">
                        +{treemapData.length - 8} more
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveSummary;
